import { useRef, type Ref } from 'react';
import { useTranslations } from 'use-intl';
import { Caption, Stack } from '@leanin/ui';
import type { MentionView } from '@leanin/shared';
import type { MentionAllGroup } from './MentionPopover';
import { mentionAllHint } from './mention-all-hint';
import {
  RichTextEditor,
  type RichTextEditorHandle,
} from '../editor/RichTextEditor';
import type { MemberView } from '../../lib/queries/identity';

// Comment input: the shared rich text editor in its bordered field variant,
// growing with its content and supporting @-mention autocomplete — the same
// v2 markdown dialect the post composer writes, rendered back with
// PostBodyRenderer. Enter inserts a newline (comments are real prose, and
// swallowing Enter is the bug the editor rework exists to fix); submit is the
// caller's button or Cmd/Ctrl+Enter. Controlled by `value`/`onChange` so the
// parent can reset it (e.g. clear on submit success).

type Props = {
  readonly id?: string;
  readonly value: string;
  readonly onChange: (markdown: string) => void;
  /** Cmd/Ctrl+Enter. The visible submit control is the caller's button. */
  readonly onSubmit: () => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  /** Scope @-mention suggestions to people who can see the post (its group /
   *  child circles). Undefined for global posts → suggest anyone. */
  readonly audienceGroupId?: string;
  /** Post author, pinned to the top of the bare-"@" suggestion list — the
   *  person most likely tagged when replying to or commenting on their post. */
  readonly mentionPriorityMember?: MemberView;
  /** Off for surfaces whose comment bodies aren't mention-resolved
   *  server-side (resources) — typing `@` stays literal text. */
  readonly mentionsEnabled?: boolean;
  /** The group "@all" addresses. Its presence IS the permission: set only
   *  for viewers who may use `@all` here (the server re-decides on write). */
  readonly mentionAllGroup?: MentionAllGroup;
  /** The saved body when this composer edits an existing comment — an edit
   *  only notifies for an `@all` the saved body did not carry. */
  readonly previousBody?: string;
  /** Resolved mentions already present in `value` (editing an existing
   *  comment) — lets the editor render its `@handle` tokens as chips. */
  readonly initialMentions?: ReadonlyArray<MentionView>;
  /** Accessible label; defaults to the add-a-comment copy. */
  readonly ariaLabel?: string;
  /** Clipboard plain text before the editor's native paste; return true to
   *  consume it (the caller inserts text / captures a link preview).
   *  `ctx.linkApplied` — the editor already linkified a non-empty selection
   *  with the pasted URL, so the caller must not insert the text itself. */
  readonly onPasteText?: (
    text: string,
    ctx: { readonly linkApplied: boolean },
  ) => boolean;
  /** Imperative access (insertText for pasted URLs, focus, clear). */
  readonly handle?: Ref<RichTextEditorHandle>;
  readonly autoFocus?: boolean;
};

export function CommentComposer({
  id,
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled = false,
  audienceGroupId,
  mentionPriorityMember,
  mentionsEnabled = true,
  mentionAllGroup,
  previousBody,
  initialMentions,
  ariaLabel,
  onPasteText,
  handle,
  autoFocus = false,
}: Props) {
  const t = useTranslations('Feed');
  const mentionNamesRef = useRef<Map<string, string>>(
    new Map(initialMentions?.map((m) => [m.handle, m.name])),
  );
  const allHint =
    mentionAllGroup !== undefined
      ? mentionAllHint(value, previousBody ?? null, mentionAllGroup, t)
      : null;
  return (
    <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
      <RichTextEditor
        value={value}
        onChange={onChange}
        variant="field"
        minHeight={24}
        disabled={disabled}
        autoFocus={autoFocus}
        onSubmitShortcut={onSubmit}
        ariaLabel={ariaLabel ?? t('comments.addLabel')}
        mentionsEnabled={mentionsEnabled}
        mentionNames={mentionNamesRef.current}
        {...(id !== undefined ? { id } : {})}
        {...(placeholder !== undefined ? { placeholder } : {})}
        {...(onPasteText !== undefined ? { onPasteText } : {})}
        {...(handle !== undefined ? { handle } : {})}
        {...(audienceGroupId !== undefined
          ? { mentionAudienceGroupId: audienceGroupId }
          : {})}
        {...(mentionPriorityMember !== undefined
          ? { mentionPriorityMember }
          : {})}
        {...(mentionAllGroup !== undefined ? { mentionAllGroup } : {})}
      />
      {allHint !== null ? <Caption aria-live="polite">{allHint}</Caption> : null}
    </Stack>
  );
}
